import { RegisterClass } from "core/Misc/typeStore";
import { Vector3 } from "core/Maths/math.vector";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

export enum RewardPartVectorPropertyType {
    Velocity,
    LastVelocity,
    ContactVelocity,
    WorldPosition,
    LastWorldPosition
}

function OperationString(operation : RewardPartVectorPropertyType) : string {
    if( typeof operation === 'number' )
        return RewardPartVectorPropertyType[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardPartVectorPropertyType as any)[op];
}

/**
 * Block used to apply math functions
 */
export class RewardPartPropertyVector extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Property", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Velocity", value: RewardPartVectorPropertyType.Velocity },
            { label: "LastVelocity", value: RewardPartVectorPropertyType.LastVelocity },
            { label: "ContactVelocity", value: RewardPartVectorPropertyType.ContactVelocity },
            { label: "WorldPosition", value: RewardPartVectorPropertyType.WorldPosition },
            { label: "LastWorldPosition", value: RewardPartVectorPropertyType.WorldPosition },
        ],
    })
    public property = RewardPartVectorPropertyType.Velocity;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerInput("part", RewardGraphConnectionPointTypes.PartID);
        this.registerOutput("output", RewardGraphConnectionPointTypes.Vector3);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardPartPropertyVector";
    }

    /**
     * Gets the left input component
     */
    public get part(): RewardGraphConnectionPoint {
        return this._inputs[0];
    }

    /**
     * Gets the reward output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;

        if (!this.part.isConnected) {
            this.output._storedFunction = null;
            this.output._storedValue = null;
            return;
        }

        switch (this.property) {
            case RewardPartVectorPropertyType.Velocity: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartVectorProperty( this.part.getConnectedValue(state), "velocity" );
                        return new Vector3(0, 0, 0);
                    };
                break;
            }
            case RewardPartVectorPropertyType.LastVelocity: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartVectorProperty( this.part.getConnectedValue(state), "last-velocity" );
                        return new Vector3(0, 0, 0);
                    };
                break;
            }
            case RewardPartVectorPropertyType.ContactVelocity: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartVectorProperty( this.part.getConnectedValue(state), "contact-velocity" );
                        return new Vector3(0, 0, 0);
                    };
                break;
            }
            case RewardPartVectorPropertyType.WorldPosition: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartVectorProperty( this.part.getConnectedValue(state), "world-position" );
                        return new Vector3(0, 0, 0);
                    };
                break;
            }
            case RewardPartVectorPropertyType.LastWorldPosition: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartVectorProperty( this.part.getConnectedValue(state), "last-world-position" );
                        return new Vector3(0, 0, 0);
                    };
                break;
            }
        }

        this.output._storedFunction = (state) => {
            return func(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardPartPropertyVector.${RewardPartVectorPropertyType[this.property]};\n`;
        return codeString;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.property = OperationString(this.property);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.property = StringOperation(serializationObject.property);
    }
}

RegisterClass("MELS.RewardPartPropertyVector", RewardPartPropertyVector);
